import React, { useState, useEffect, useCallback } from "react";
import { useOutletContext, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { theme } from "@/lib/theme";
import ImagesGrid from "@/components/chitra/ImagesGrid";
// Using fullscreen modals provided by ChitraLayout via Outlet context
import { ChitraImageGridSkeleton } from "@/components/ChitraImageGridSkeleton";
import { ChitraNavigation } from "@/components/chitra/ChitraNavigation";
import { ChitraInputArea } from "@/components/chitra/ChitraInputArea";
import { useToast } from "@/hooks/use-toast";

type GeneratedImage = {
    id?: string;
    url: string;
    uploaded: boolean;
    uploading: boolean;
    prompt?: string;
    style?: string;
    cloudinaryUrl?: string;
    publicId?: string;
    createdAt?: string | Date;
    aspect_ratio?: string;
    cloudinary_public_id?: string;
    image_url?: string;
    likes_count?: number;
    user_id?: string;
    creator_name?: string;
    creator_avatar?: string;
    video_id?: string;
    video_url?: string;
    thumbnail_url?: string;
    status?: "pending" | "processing" | "completed" | "failed";
    isVideo?: boolean;
    isGeneratingPlaceholder?: boolean;
    processingToolName?: string;
    attached_media?: string[] | null;
};

type ContextType = {
    isDarkMode: boolean;
    user: any;
    openImageFullscreen: (img: any, onUseImage?: (dataUrl: string) => void) => void;
    openVideoFullscreen: (video: any) => void;
};

export default function ChitraLibrary() {
    const { isDarkMode, user, openImageFullscreen, openVideoFullscreen } = useOutletContext<ContextType>();
    const location = useLocation();

    // Gallery state
    const [generatedImages, setGeneratedImages] = useState<GeneratedImage[]>([]);
    const [videoGenerations, setVideoGenerations] = useState<any[]>([]);
    const [visibleImages, setVisibleImages] = useState(12);
    // Fullscreen handled by ChitraLayout via context
    const [isHovered, setIsHovered] = useState<string | null>(null);
    const [isGalleryLoading, setIsGalleryLoading] = useState(false);
    const [imageFilter, setImageFilter] = useState<"all" | "images" | "videos">("all");
    const [sortBy, setSortBy] = useState<'recent' | 'likes'>('recent');

    // Generation state
    const [prompt, setPrompt] = useState("");
    const [isGenerating, setIsGenerating] = useState(false);
    const [attachedImages, setAttachedImages] = useState<string[]>([]);
    const [isEditMode] = useState(false);
    const [isEditingImage] = useState(false);
    const [isProcessingTool, setIsProcessingTool] = useState(false);
    const [processingToolName, setProcessingToolName] = useState("");
    const [isEnhancingPrompt, setIsEnhancingPrompt] = useState(false);
    const { toast } = useToast();

    const fetchGeneratedImages = useCallback(async () => {
        if (!user?.id) {
            setGeneratedImages([]);
            setVideoGenerations([]);
            return;
        }
        try {
            setIsGalleryLoading(true);
            const { data, error } = await supabase.functions.invoke("get-gallery-images", {
                body: {
                    offset: 0,
                    limit: 1000,
                    userId: user.id,
                    sortBy: sortBy,
                    ownerOnly: true,
                },
            });
            if (error || !data?.success) throw error || new Error("Failed to fetch images");

            const images = (data.images || []) as any[];
            const videosResp = (data.videos || []) as any[];

            const formattedVideos = videosResp.map((v: any) => ({
                id: v.id,
                video_id: v.video_id,
                url: v.thumbnail_url || v.video_url || "",
                video_url: v.video_url,
                thumbnail_url: v.thumbnail_url,
                status: v.status,
                uploaded: v.status === "completed",
                uploading: v.status === "pending" || v.status === "processing",
                prompt: v.prompt,
                createdAt: v.created_at,
                user_id: v.user_id,
                isVideo: true,
            } as GeneratedImage));

            const formattedImages: GeneratedImage[] = images.map((img: any) => ({
                id: img.id,
                url: img.image_url || "",
                uploaded: true,
                uploading: false,
                prompt: img.prompt,
                style: img.style,
                cloudinaryUrl: img.image_url,
                publicId: undefined,
                createdAt: img.created_at,
                aspect_ratio: img.aspect_ratio,
                likes_count: img.likes_count,
                user_id: img.user_id,
                creator_name: "",
                creator_avatar: user.user_metadata?.avatar_url,
                attached_media: (img as any).attached_media || null,
            }));

            setGeneratedImages(formattedImages);
            setVideoGenerations(formattedVideos);
        } catch (error) {
            console.error("Error fetching library images:", error);
        } finally {
            setIsGalleryLoading(false);
        }
    }, [user?.id, sortBy]);

    useEffect(() => {
        fetchGeneratedImages();
    }, [fetchGeneratedImages]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const imageId = params.get('imageId');
        const videoId = params.get('videoId');

        if (isGalleryLoading) return;

        if (imageId && generatedImages.length > 0) {
            const img = generatedImages.find((i) => i.id === imageId);
            if (img) {
                openImageFullscreen(img);
            }
        } else if (videoId && videoGenerations.length > 0) {
            const vid = videoGenerations.find((v) => v.video_id === videoId || v.id === videoId);
            if (vid) {
                openVideoFullscreen(vid);
            }
        }
    }, [location.search, isGalleryLoading, generatedImages, videoGenerations, openImageFullscreen, openVideoFullscreen]);

    const filteredItems = React.useMemo(() => {
        const videos = videoGenerations.map((v) => ({
            id: v.id,
            url: v.video_url || v.thumbnail_url || "",
            video_url: v.video_url,
            thumbnail_url: v.thumbnail_url,
            uploaded: v.status === "completed",
            uploading: v.status === "pending" || v.status === "processing",
            prompt: v.prompt,
            style: "Video",
            createdAt: v.created_at,
            user_id: v.user_id,
            video_id: v.video_id,
            status: v.status,
            isVideo: true,
        } as GeneratedImage));

        let items: GeneratedImage[] = [];
        if (imageFilter === "videos") {
            items = videos;
        } else if (imageFilter === 'images') {
            items = [...generatedImages];
        } else {
            items = [...videos, ...generatedImages];
        }

        if (isGenerating) {
            const placeholder: GeneratedImage = {
                id: "generating-placeholder",
                url: "",
                uploaded: false,
                uploading: true,
                isGeneratingPlaceholder: true,
                processingToolName: processingToolName,
                createdAt: new Date().toISOString(),
            } as any;
            items.unshift(placeholder);
        }

        // Sort by likes or recent (fallback client sort)
        items.sort((a, b) => {
            if (sortBy === 'likes') {
                const aLikes = a.likes_count ?? 0;
                const bLikes = b.likes_count ?? 0;
                if (bLikes !== aLikes) return bLikes - aLikes;
            }
            const aTime = new Date(a.createdAt || 0).getTime();
            const bTime = new Date(b.createdAt || 0).getTime();
            return bTime - aTime;
        });

        return items;
    }, [videoGenerations, generatedImages, imageFilter, sortBy, isGenerating, processingToolName]);

    // Downloads are handled within the shared fullscreen modal in layout

    const uploadBase64ToCloudinary = async (base64Data: string): Promise<string> => {
        try {
            const cloudName = "dcqtcd2zs";
            const uploadPreset = "image_server_1";
            const folder = "images";

            const prepareBase64 = (src: string) => new Promise<string>((resolve, reject) => {
                if (!src.startsWith('data:')) return resolve(src);
                const img = new Image();
                img.crossOrigin = 'anonymous';
                img.onload = () => {
                    const maxW = 1280;
                    const maxH = 1280;
                    let w = img.naturalWidth || img.width;
                    let h = img.naturalHeight || img.height;
                    const scale = Math.min(1, maxW / Math.max(1, w), maxH / Math.max(1, h));
                    w = Math.max(1, Math.round(w * scale));
                    h = Math.max(1, Math.round(h * scale));
                    const canvas = document.createElement('canvas');
                    canvas.width = w;
                    canvas.height = h;
                    const ctx = canvas.getContext('2d');
                    if (!ctx) return resolve(src);
                    ctx.drawImage(img, 0, 0, w, h);
                    try {
                        const out = canvas.toDataURL('image/jpeg', 0.7);
                        resolve(out);
                    } catch {
                        resolve(src);
                    }
                };
                img.onerror = () => resolve(src);
                img.src = src;
            });

            const optimizedBase64 = await prepareBase64(base64Data);

            const formData = new FormData();
            formData.append("file", optimizedBase64);
            formData.append("upload_preset", uploadPreset);
            formData.append("folder", folder);

            const response = await fetch(`https://api.cloudinary.com/v1_1/${cloudName}/image/upload`, {
                method: "POST",
                body: formData,
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Cloudinary upload failed: ${response.status} ${errorText}`);
            }

            const data = await response.json();
            return data.secure_url;
        } catch (error: any) {
            console.error("Input upload error:", error);
            throw error;
        }
    };

    const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files ? Array.from(event.target.files) : [];
        const images = files.filter((f) => f.type.startsWith("image/"));
        if (images.length === 0) return;

        const remainingSlots = Math.max(0, 4 - attachedImages.length);
        const count = Math.min(remainingSlots, images.length);
        const toProcess = images.slice(0, count);

        if (count === 0) return;

        // show placeholders
        setAttachedImages((prev) => {
            const next = prev.slice(0, 4);
            const placeholders = Array(count).fill("LOADING");
            return [...next, ...placeholders].slice(0, 4);
        });

        toProcess.forEach((file) => {
            const reader = new FileReader();
            reader.onload = async (e) => {
                try {
                    const dataUrl = e.target?.result as string;
                    const finalUrl = dataUrl.startsWith('data:')
                        ? await uploadBase64ToCloudinary(dataUrl)
                        : dataUrl;
                    setAttachedImages((prev) => {
                        const idx = prev.indexOf("LOADING");
                        if (idx !== -1) {
                            const next = [...prev];
                            next[idx] = finalUrl;
                            return next;
                        }
                        return prev.length < 4 ? [...prev, finalUrl] : prev;
                    });
                } catch (err) {
                    setAttachedImages((prev) => {
                        const idx = prev.indexOf("LOADING");
                        if (idx !== -1) {
                            const next = [...prev];
                            next.splice(idx, 1);
                            return next;
                        }
                        return prev;
                    });
                    toast({ title: "Attach failed", description: "Could not upload some images.", variant: "destructive" });
                }
            };
            reader.onerror = () => {
                setAttachedImages((prev) => {
                    const idx = prev.indexOf("LOADING");
                    if (idx !== -1) {
                        const next = [...prev];
                        next.splice(idx, 1);
                        return next;
                    }
                    return prev;
                });
                toast({ title: "Attach failed", description: "Could not read image.", variant: "destructive" });
            };
            reader.readAsDataURL(file);
        });
    };

    const handleGenerate = async () => {
        if (!prompt.trim()) return;
        try {
            setIsGenerating(true);

            // Upload any locally attached images and keep only valid URLs
            const uploadedAttachedUrls = await Promise.all(
                attachedImages.map(async (img) =>
                    img.startsWith('data:') ? await uploadBase64ToCloudinary(img) : img
                )
            );
            const validAttachedUrls = uploadedAttachedUrls.filter(
                (u) => typeof u === 'string' && /^https?:\/\//.test(u)
            );

            const baseBody = {
                prompt: prompt.trim(),
                images: validAttachedUrls,
                aspectRatio: "3:2",
                style: "Auto",
                userId: user?.id,
            };

            let data: any;
            let error: any;

            if (validAttachedUrls.length > 0) {
                ({ data, error } = await supabase.functions.invoke("imageEdit", {
                    body: baseBody,
                }));
            } else {
                ({ data, error } = await supabase.functions.invoke("universal-image-generation", {
                    body: { ...baseBody, origin: 'chitra-library' },
                }));
            }

            if (error || !data?.success || !data?.image) {
                throw new Error(error?.message || data?.error || "Generation failed");
            }

            const newImage: GeneratedImage = {
                id: data.databaseId,
                url: data.image,
                uploaded: true,
                uploading: false,
                prompt: prompt.trim(),
                style: "Auto",
                aspect_ratio: "3:2",
                createdAt: new Date().toISOString(),
                user_id: user?.id,
                cloudinaryUrl: data.image,
                image_url: data.image,
                attached_media: validAttachedUrls.length ? validAttachedUrls : null,
            };

            setGeneratedImages((prev) => [newImage, ...prev]);
        } catch (e: any) {
            toast({ title: "Error", description: e.message, variant: "destructive" });
        } finally {
            setIsGenerating(false);
            setPrompt("");
            setAttachedImages([]);
        }
    };

    const enhancePrompt = async () => {
        if (!prompt.trim()) {
            toast({ title: "Error", description: "Please enter a prompt first", variant: "destructive" });
            return;
        }
        setIsEnhancingPrompt(true);
        try {
            const uploadedImageUrls = await Promise.all(
                attachedImages.map(async (img) =>
                    img.startsWith('data:') ? await uploadBase64ToCloudinary(img) : img
                )
            );
            const validImageUrls = uploadedImageUrls.filter(
                (u) => typeof u === 'string' && /^https?:\/\//.test(u)
            );

            const { data, error } = await supabase.functions.invoke("quickTask", {
                body: { prompt: prompt.trim(), images: validImageUrls, mode: 'enhance' },
            });
            if (error || !data?.success) throw new Error(data?.error || error?.message);
            if (data.enhancedPrompt) setPrompt(data.enhancedPrompt);
        } catch (error: any) {
            toast({ title: "Error", description: error.message, variant: "destructive" });
        } finally {
            setIsEnhancingPrompt(false);
        }
    };

    return (
        <div className="max-w-7xl mx-auto">
            <div className="mb-6 flex items-center justify-between gap-3 flex-wrap">
                <h2 className="text-2xl instrument-serif-regular select-none" style={{ color: theme.text(isDarkMode) }}>
                    My Library
                </h2>
                <ChitraNavigation
                    isDarkMode={isDarkMode}
                    filter={imageFilter}
                    onFilterChange={(val) => setImageFilter(val as any)}
                    sortBy={sortBy}
                    onSortChange={setSortBy}
                    filterOptions={[
                        { value: "all", label: "All" },
                        { value: "images", label: "Image" },
                        { value: "videos", label: "Video" },
                    ]}
                />
            </div>


            {isGalleryLoading ? (
                <ChitraImageGridSkeleton isDarkMode={isDarkMode} />
            ) : filteredItems.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                    <p className={cn("text-lg", isDarkMode ? "text-gray-400" : "text-gray-600")}>You haven't generated any media yet.</p>
                </div>
            ) : (
                <ImagesGrid
                    images={filteredItems as any}
                    visibleCount={visibleImages}
                    isDarkMode={isDarkMode}
                    isHovered={isHovered}
                    setIsHovered={setIsHovered}
                    onOpenFullscreen={(img) => {
                        openImageFullscreen(img);
                    }}
                    user={user}
                    onShowMore={() => setVisibleImages((prev) => prev + 12)}
                    isLoading={isGalleryLoading}
                    onOpenVideoFullscreen={(video) => openVideoFullscreen(video)}
                />
            )}

            {/* Bottom gradient overlay - positioned behind input area */}
            <div
                className="fixed bottom-0 left-0 right-0 h-24 pointer-events-none z-10"
                style={{
                    background: isDarkMode
                        ? 'linear-gradient(to top, rgba(18,18,18,0.95) 0%, rgba(18,18,18,0.85) 20%, rgba(18,18,18,0.4) 45%, transparent 100%)'
                        : 'linear-gradient(to top, rgba(248,248,248,0.95) 0%, rgba(248,248,248,0.85) 20%, rgba(248,248,248,0.4) 45%, transparent 100%)'
                }}
            />

            {/* Input area for generating new images */}
            <div className="mt-6">
                <ChitraInputArea
                    prompt={prompt}
                    setPrompt={setPrompt}
                    isGenerating={isGenerating}
                    isEditingImage={isEditingImage}
                    isEnhancingPrompt={isEnhancingPrompt}
                    isEditMode={isEditMode}
                    attachedImages={attachedImages}
                    selectedAspectRatio="3:2"
                    showAspectRatios={false}
                    setShowAspectRatios={() => { }}
                    isDarkMode={isDarkMode}
                    onGenerate={handleGenerate}
                    onEditImage={() => { }}
                    onEnhancePrompt={enhancePrompt}
                    onImageUpload={handleImageUpload}
                    onClearAttachedImage={() => { setAttachedImages([]); setPrompt(""); }}
                    onRemoveAttachedImage={(index) => {
                        setAttachedImages((prev) => {
                            const next = prev.slice();
                            next.splice(index, 1);
                            return next;
                        });
                    }}
                    onUpdateAttachedImage={() => { }}
                    onAddAttachedImage={() => { }}
                    onAspectRatioSelect={() => { }}
                    onToolProcessing={(isProcessing, toolName) => { setIsProcessingTool(isProcessing); setProcessingToolName(toolName); }}
                    onToolComplete={() => { }}
                    onVideoModeClick={() => { }}
                    onGenerateVideo={() => { }}
                    isVideoMode={false}
                    videoAvatarPreviewUrl=""
                    videoBackgroundUrl=""
                    onExitVideoMode={() => { }}
                />
            </div>
        </div>
    );
}
